import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Briefcase, MessagesSquare, Clock, Mail, Phone, MessageSquare, MoreHorizontal, ArrowRight, Trash2, AlertTriangle, Copy, CalendarPlus, Eye, MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn, dateLocale } from "@/lib/utils";
import { Section, Empty, fmtD, daysAgo, pInitials, ACT_TYPE_LABELS, STAGE_TONE, completionTone, SemanticProgress } from "./teamShared";
import { translateFr as tfr } from "@/i18n";

const Kpi = ({ icon: Icon, label, value, testid, onClick }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="rounded-2xl border border-border bg-card shadow-sm dark:shadow-none p-3.5 text-left transition-colors hover:border-foreground/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer">
    <div className="flex items-center gap-2 text-[11px] text-muted-foreground"><Icon className="h-3.5 w-3.5" aria-hidden />{label}</div>
    <div className="text-xl font-semibold tabular-nums mt-1">{value}</div>
  </button>
);

export default function TeamOverview({ ov, onGoTab, onChanged, onPlanAction }) {  const { club, kpis, primary_contact, athletes, opportunities, recent_activities, upcoming_events, recent_documents, notes, health } = ov;
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const lastDays = daysAgo(kpis.last_contact);

  const addNote = async () => {
    if (!note.trim() || savingNote) return;
    setSavingNote(true);
    try {
      await api.post(`/clubs/${club.id}/notes`, { content: note.trim() });
      setNote("");
      onChanged();
      toast.success(tfr("Note ajoutée"));
    } catch { toast.error(tfr("Impossible d'ajouter la note")); } finally { setSavingNote(false); }
  };
  const delNote = async (nid) => {
    try { await api.delete(`/clubs/${club.id}/notes/${nid}`); onChanged(); } catch { toast.error(tfr("Suppression impossible")); }
  };

  const TabLink = ({ tab, children }) => (
    <button onClick={() => onGoTab(tab)} className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1" data-testid={`ov-link-${tab}`}>
      {children} <ArrowRight className="h-3 w-3" />
    </button>
  );

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="team-kpis">
        <Kpi icon={Users} label={tfr("Athlètes liés")} value={kpis.linked_athletes} testid="kpi-athletes" onClick={() => onGoTab("athletes")} />
        <Kpi icon={Briefcase} label={tfr("Opportunités actives")} value={kpis.active_opportunities} testid="kpi-opps" onClick={() => onGoTab("opportunities")} />
        <Kpi icon={MessagesSquare} label={tfr("Interactions (30 j)")} value={kpis.interactions_30d} testid="kpi-interactions" onClick={() => onGoTab("activities")} />
        <Kpi icon={Clock} label={tfr("Dernier échange")} value={lastDays == null ? "—" : lastDays === 0 ? tfr("Aujourd'hui") : (dateLocale() === "en-GB" ? `${lastDays}d ago` : `Il y a ${lastDays} j`)} testid="kpi-last-contact" onClick={() => onGoTab("activities")} />
      </div>

      <Checklist ov={ov} onGoTab={onGoTab} onPlanAction={onPlanAction} />

      {/* Prochaine action (distinct des rendez-vous) */}
      <Section title={tfr("Prochaine action")} testid="ov-next-action"
        action={<button onClick={onPlanAction} className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="ov-plan-action">{tfr("+ Planifier une action")}</button>}>
        {ov.next_action ? (
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{ov.next_action.title}</p>
              <p className="text-[11px] text-muted-foreground">{fmtD(ov.next_action.starts_at)}</p>
            </div>
            <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="ov-next-action-done"
              onClick={async () => {
                try { await api.patch(`/events/${ov.next_action.id}`, { status: "done" }); toast.success(tfr("Action terminée")); onChanged(); }
                catch { toast.error(tfr("Action impossible")); }
              }}>
              Marquer comme fait
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">{tfr("Aucune action planifiée. Planifiez une relance, un appel ou un envoi de profils.")}</p>
        )}
      </Section>

      {/* Needs attention */}
      <div className="rounded-2xl border border-border bg-card shadow-sm dark:shadow-none p-4" data-testid="team-health">
        {health.score == null ? (
          <div>
            <p className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-muted-foreground" aria-hidden /> {tfr("Santé de la relation :")} <span className="text-muted-foreground font-normal">{tfr("Données insuffisantes")}</span></p>
            <p className="text-xs text-muted-foreground mt-1" data-testid="health-insufficient">
              {tfr("Ajoutez un contact et quelques interactions pour permettre à Athlynx d'évaluer cette relation.")}
            </p>
            <button onClick={() => onGoTab("relations")} className="mt-2 text-xs font-medium text-athlynx hover:underline">{tfr("+ Ajouter un contact")}</button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-athlynx" aria-hidden />
              <p className="text-sm font-semibold">{tfr("Santé de la relation :")} <span className="tabular-nums">{health.score} /100</span></p>
              <button onClick={() => setWhyOpen((v) => !v)} className="text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto underline underline-offset-2" data-testid="health-why">
                {tfr("Pourquoi ce score ?")}
              </button>
            </div>
            {whyOpen && (
              <ul className="mt-2 space-y-1" data-testid="health-reasons">
                {health.reasons.map((r, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <span className="h-1 w-1 rounded-full bg-current mt-1.5 shrink-0" />{r}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Informations */}
        <Section title="Informations" testid="ov-info">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            {[["Nom complet", club.name], ["Sport", club.sport], ["Niveau", club.competition || club.league || "—"],
              ["Pays", club.country || "—"], ["Ville", club.city || "—"],
              ["Site web", club.website || "—"], [tfr("Statut relation"), club.relationship_status || "—"], [tfr("Priorité"), club.priority || "—"]].map(([k, v]) => (
              <div key={k}><dt className="text-muted-foreground">{k}</dt><dd className="text-foreground font-medium mt-0.5 break-words">{v}</dd></div>
            ))}
          </dl>
        </Section>

        {/* Contact principal */}
        <Section title="Contact principal" action={<TabLink tab="relations">Relations</TabLink>} testid="ov-contact">
          {primary_contact ? (
            <PrimaryContact contact={primary_contact} onGoTab={onGoTab} onPlanAction={onPlanAction} />
          ) : (
            <Empty text={tfr("Ajoutez votre interlocuteur principal pour centraliser vos échanges avec cette équipe.")} cta={tfr("Ajouter un contact")} onClick={() => onGoTab("relations")} testid="ov-contact-empty" />
          )}
        </Section>

        {/* Prochains rendez-vous */}
        <Section title={tfr("Prochains rendez-vous")} action={<Link to="/calendar" className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">Calendrier</Link>} testid="ov-events">
          {upcoming_events.length ? upcoming_events.map((e) => (
            <div key={e.id} className="flex items-center justify-between py-1.5 border-b border-border/60 last:border-0 text-xs">
              <span className="font-medium truncate">{e.title}</span>
              <span className="text-muted-foreground tabular-nums shrink-0 ml-2">{fmtD(e.starts_at)}</span>
            </div>
          )) : <Empty text={tfr("Aucun rendez-vous à venir.")} cta={tfr("Planifier une action")} onClick={onPlanAction} testid="ov-events-empty" />}
        </Section>

        {/* Opportunités en cours */}
        <Section title={tfr("Opportunités en cours")} action={<TabLink tab="opportunities">{tfr("Tout voir")}</TabLink>} testid="ov-opps">
          {opportunities.length ? opportunities.map((o) => (
            <div key={o.id} className="flex items-center justify-between py-1.5 border-b border-border/60 last:border-0 text-xs gap-2">
              <span className="font-medium truncate">{o.title}</span>
              <span className={cn("text-[10px] rounded-full border px-2 py-0.5 shrink-0", STAGE_TONE[o.status])}>{tfr(o.status)}</span>
            </div>
          )) : <Empty text={tfr("Aucune opportunité active.")} cta={tfr("Créer une opportunité")} onClick={() => onGoTab("opportunities")} testid="ov-opps-empty" />}
        </Section>

        {/* Dernières activités */}
        <Section title={tfr("Dernières activités")} action={<TabLink tab="activities">{tfr("Tout voir")}</TabLink>} testid="ov-activities">
          {recent_activities.length ? recent_activities.map((a) => (
            <div key={a.id} className="flex items-center justify-between py-1.5 border-b border-border/60 last:border-0 text-xs gap-2">
              <span className="truncate"><span className="text-muted-foreground">{ACT_TYPE_LABELS[a.type] || a.type} · </span>{a.title}</span>
              <span className="text-muted-foreground tabular-nums shrink-0">{fmtD(a.occurred_at)}</span>
            </div>
          )) : <Empty text={tfr("Les interactions significatives apparaîtront ici.")} cta={tfr("Ajouter une activité")} onClick={() => onGoTab("activities")} testid="ov-activities-empty" />}
        </Section>

        {/* Athlètes liés */}
        <Section title={tfr("Athlètes liés")} action={<TabLink tab="athletes">{tfr("Tout voir")}</TabLink>} testid="ov-athletes">
          {athletes.length ? (
            <div className="space-y-1.5">
              {athletes.slice(0, 5).map((l) => (
                <Link key={l.id} to={`/players/${l.player.id}`} className="flex items-center gap-2.5 rounded-xl border border-border bg-secondary/30 hover:bg-accent px-2.5 py-2 transition-colors">
                  <span className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-[10px] font-semibold shrink-0">{pInitials(l.player.name)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold truncate">{l.player.name}</span>
                    <span className="block text-[10px] text-muted-foreground truncate">
                      {[l.player.position, l.player.age ? `${l.player.age} ans` : null, l.player.sport].filter(Boolean).join(" · ") || "—"}
                    </span>
                  </span>
                  <span className="text-[9px] rounded-full border border-border bg-secondary/60 px-1.5 py-0.5 text-muted-foreground shrink-0">{tfr(l.status)}</span>
                </Link>
              ))}
              {athletes.length > 5 && (
                <button onClick={() => onGoTab("athletes")} className="text-[11px] text-muted-foreground hover:text-foreground">+{athletes.length - 5} autres</button>
              )}
            </div>
          ) : <Empty text={tfr("Aucun athlète associé à cette équipe.")} cta={tfr("Associer un athlète")} onClick={() => onGoTab("athletes")} testid="ov-athletes-empty" />}
        </Section>

        {/* Documents récents */}
        <Section title={tfr("Documents récents")} action={<TabLink tab="documents">{tfr("Tout voir")}</TabLink>} testid="ov-docs">
          {recent_documents.length ? recent_documents.map((d) => (
            <div key={d.id} className="flex items-center justify-between py-1.5 border-b border-border/60 last:border-0 text-xs gap-2">
              <span className="font-medium truncate">{d.label}</span>
              <span className="text-muted-foreground tabular-nums shrink-0">{fmtD(d.created_at)}</span>
            </div>
          )) : <Empty text={tfr("Aucun document lié.")} cta={tfr("Ajouter un document")} onClick={() => onGoTab("documents")} testid="ov-docs-empty" />}
        </Section>
      </div>

      {/* Notes internes */}
      <Section title="Notes internes" testid="ov-notes">
        <div className="flex gap-2">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={tfr("Ajouter une note interne…")} className="resize-none text-sm" data-testid="ov-note-input" />
          <Button size="sm" onClick={addNote} disabled={!note.trim() || savingNote} className="h-auto bg-athlynx text-white hover:bg-athlynx/90" data-testid="ov-note-save">{tfr("Ajouter")}</Button>
        </div>
        <div className="mt-3 space-y-2">
          {notes.map((n) => (
            <div key={n.id} className="rounded-xl border border-border bg-secondary/30 p-2.5 text-xs" data-testid={`ov-note-${n.id}`}>
              <p className="whitespace-pre-wrap">{n.content}</p>
              <div className="flex items-center justify-between mt-1.5 text-[10px] text-muted-foreground">
                <span>{n.author} · {fmtD(n.created_at)}</span>
                <button onClick={() => delNote(n.id)} aria-label={tfr("Supprimer la note")} className="hover:text-red-500"><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function PrimaryContact({ contact, onGoTab, onPlanAction }) {
  const telHref = (contact.phone || "").replace(/[^\d+]/g, "");
  const qa = "h-9 w-9 rounded-lg border border-border bg-secondary/30 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent hover:border-foreground/25 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const copy = (v, msg) => { navigator.clipboard?.writeText(v).then(() => toast.success(msg)).catch(() => {}); };
  return (
    <div className="flex flex-wrap items-center gap-3">
      {contact.photo_b64 ? (
        <img src={contact.photo_b64} alt={contact.name} className="h-10 w-10 rounded-full object-cover shrink-0" />
      ) : (
        <span className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold shrink-0">{pInitials(contact.name)}</span>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate">{contact.name}</p>
        <p className="text-[11px] text-muted-foreground truncate">{contact.role || "—"}</p>
      </div>
      <div className="flex items-center gap-1.5 ml-auto">
        {telHref && (
          <a href={`tel:${telHref}`} title={`Appeler ${contact.name}`} aria-label={`Appeler ${contact.name}`} className={qa} data-testid="ov-contact-call">
            <Phone className="h-4 w-4" aria-hidden />
          </a>
        )}
        {telHref && (
          <a href={`sms:${telHref}`} title={`${tfr("Envoyer un message à")} ${contact.name}`} aria-label={`${tfr("Envoyer un message à")} ${contact.name}`} className={qa} data-testid="ov-contact-sms">
            <MessageSquare className="h-4 w-4" aria-hidden />
          </a>
        )}
        {contact.email && (
          <a href={`mailto:${contact.email}`} title={`${tfr("Envoyer un email à")} ${contact.name}`} aria-label={`${tfr("Envoyer un email à")} ${contact.name}`} className={qa} data-testid="ov-contact-email">
            <Mail className="h-4 w-4" aria-hidden />
          </a>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button title={`Plus d'actions pour ${contact.name}`} aria-label={`Plus d'actions pour ${contact.name}`} className={qa} data-testid="ov-contact-more">
              <MoreHorizontal className="h-4 w-4" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            <DropdownMenuItem onClick={() => onGoTab("relations")} data-testid="ov-contact-view" className="text-xs gap-2">
              <Eye className="h-3.5 w-3.5" aria-hidden /> Voir le contact
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onGoTab("activities")} data-testid="ov-contact-add-interaction" className="text-xs gap-2">
              <MessageSquarePlus className="h-3.5 w-3.5" aria-hidden /> Ajouter une interaction
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onPlanAction} data-testid="ov-contact-plan" className="text-xs gap-2">
              <CalendarPlus className="h-3.5 w-3.5" aria-hidden /> Planifier une action
            </DropdownMenuItem>
            {contact.phone && (
              <DropdownMenuItem onClick={() => copy(contact.phone, tfr("Numéro copié"))} data-testid="ov-contact-copy-phone" className="text-xs gap-2">
                <Copy className="h-3.5 w-3.5" aria-hidden /> Copier le numéro
              </DropdownMenuItem>
            )}
            {contact.email && (
              <DropdownMenuItem onClick={() => copy(contact.email, tfr("Email copié"))} data-testid="ov-contact-copy-email" className="text-xs gap-2">
                <Copy className="h-3.5 w-3.5" aria-hidden /> Copier l'email
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function Checklist({ ov, onGoTab, onPlanAction }) {
  const [showDetails, setShowDetails] = useState(false);
  const { club, kpis, primary_contact } = ov;
  const items = [
    { label: tfr("Équipe créée"), done: true },
    { label: tfr("Associer un athlète"), done: kpis.linked_athletes > 0, go: () => onGoTab("athletes") },
    { label: tfr("Ajouter un contact principal"), done: !!primary_contact, go: () => onGoTab("relations") },
    { label: tfr("Définir le statut de la relation"), done: !!club.relationship_status, go: () => onGoTab("relations") },
    { label: tfr("Définir une priorité"), done: !!club.priority, go: () => onGoTab("relations") },
    { label: tfr("Planifier une prochaine action"), done: !!ov.next_action, go: onPlanAction },
    { label: tfr("Ajouter une opportunité"), done: kpis.active_opportunities > 0, go: () => onGoTab("opportunities") },
  ];
  const done = items.filter((i) => i.done).length;
  const pct = Math.round((100 * done) / items.length);
  const tone = completionTone(pct);
  const complete = pct === 100;
  const stepsTitle = `${done} ${tfr("étapes complétées sur")} ${items.length}`;
  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm dark:shadow-none p-4" data-testid="team-checklist">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold" data-testid="team-checklist-title">
          {complete ? (
            <><span className="text-emerald-600 dark:text-emerald-400" aria-hidden>✓</span> {tfr("Relation configurée")}</>
          ) : (
            <>Complétez votre relation avec {club.name}</>
          )}
        </p>
        <span className={cn("text-xs font-medium tabular-nums", tone.text)} title={stepsTitle} data-testid="team-checklist-pct">{pct} %</span>
      </div>
      <SemanticProgress value={pct} className="mt-2" testid="team-checklist-bar"
        label={`${tfr("Configuration de la relation :")} ${pct} %`} title={stepsTitle} />
      <div className="flex items-center justify-between gap-2 mt-1.5">
        <span className="text-[11px] text-muted-foreground tabular-nums" data-testid="team-checklist-count">{done}/{items.length} étapes complétées</span>
        {complete && (
          <button type="button" onClick={() => setShowDetails((v) => !v)}
            className="text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
            data-testid="team-checklist-details">
            {showDetails ? tfr("Masquer les détails") : tfr("Voir les détails")}
          </button>
        )}
      </div>
      {(!complete || showDetails) && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
          {items.map((it) => (
            <button key={it.label} type="button" disabled={it.done} onClick={it.go}
              data-testid={`checklist-${it.label}`}
              className={cnCheck(it.done)}>
              <span aria-hidden className={it.done ? undefined : tone.text}>{it.done ? "✓" : "○"}</span> {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function cnCheck(done) {
  return done
    ? "flex items-center gap-2 text-xs text-muted-foreground/70 text-left cursor-default"
    : "flex items-center gap-2 text-xs text-foreground text-left hover:text-athlynx transition-colors";
}
